import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * StudyCallButton - FaceTime-style Study Together
 *
 * 2025 UX: Shows recent study buddies for quick reconnect,
 * or generate a new code to invite someone new.
 *
 * FERPA/COPPA: Only displays user-provided nicknames, no PII.
 */
const StudyCallButton = ({ user, userTier, sessionContext, onCodeGenerated, onCodeToastChange, roomName, compact = false, onOpenStudyCodeSheet }) => {
  const [studyCode, setStudyCode] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showBuddyPicker, setShowBuddyPicker] = useState(false);
  const [recentBuddies, setRecentBuddies] = useState([]);
  const [loadingBuddies, setLoadingBuddies] = useState(false);
  const [invitingBuddy, setInvitingBuddy] = useState(null);

  // Fetch recent buddies when picker opens
  useEffect(() => {
    if (showBuddyPicker && user?.id) {
      fetchRecentBuddies();
    }
  }, [showBuddyPicker, user?.id]);

  const fetchRecentBuddies = async () => {
    setLoadingBuddies(true);
    try {
      const response = await fetch('/api/recent-study-buddies', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        setRecentBuddies(data.buddies || []);
      }
    } catch (error) {
      console.error('Failed to fetch study buddies:', error);
    } finally {
      setLoadingBuddies(false);
    }
  };

  const generateAndShowCode = async () => {
    setIsGenerating(true);
    setShowBuddyPicker(false);

    // If parent provided premium StudyCodeSheet handler, use it
    if (onOpenStudyCodeSheet) {
      setIsGenerating(false);
      onOpenStudyCodeSheet();
      return;
    }

    // Fallback to internal toast (for backwards compatibility)
    try {
      const response = await fetch('/api/create-study-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user?.id || 'guest',
          student_name: user?.name || 'Student',
          subject: sessionContext?.subject || 'homework',
          problem_context: sessionContext?.current_problem || 'Study help needed'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setStudyCode(data.code);
        setShowToast(true);

        if (onCodeGenerated) {
          onCodeGenerated(data.code);
        }

        if (onCodeToastChange) {
          onCodeToastChange(true);
        }

        // Auto-hide toast after 30 seconds
        setTimeout(() => {
          setShowToast(false);
          if (onCodeToastChange) {
            onCodeToastChange(false);
          }
        }, 30000);
      }
    } catch (error) {
      console.error('Failed to generate code:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const inviteBuddyDirect = async (buddy) => {
    if (!roomName) {
      // No active room, generate code instead
      generateAndShowCode();
      return;
    }

    setInvitingBuddy(buddy.id);

    try {
      const response = await fetch('/api/invite-friend-direct', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          connection_id: buddy.id,
          room_name: roomName
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Show the invite URL in toast
        setStudyCode(null); // Clear any existing code
        setShowBuddyPicker(false);

        // Copy invite link and show confirmation
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(data.invite_url);
        }

        // Show success toast
        setShowToast(true);
        if (onCodeToastChange) {
          onCodeToastChange(true);
        }

        // Auto-hide after 10 seconds
        setTimeout(() => {
          setShowToast(false);
          if (onCodeToastChange) {
            onCodeToastChange(false);
          }
        }, 10000);
      }
    } catch (error) {
      console.error('Failed to invite buddy:', error);
    } finally {
      setInvitingBuddy(null);
    }
  };

  const copyCode = () => {
    const shareMsg = `Join my study session! Code: ${studyCode}`;
    navigator.clipboard.writeText(shareMsg);
  };

  const shareCode = async () => {
    const shareMsg = `Join my study session! Code: ${studyCode}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Study Session',
          text: shareMsg
        });
      } catch (error) {
        copyCode();
      }
    } else {
      copyCode();
    }
  };

  const handleButtonClick = () => {
    // If user is authenticated and might have buddies, show picker first
    if (user?.id) {
      setShowBuddyPicker(true);
    } else {
      // Guest users just generate code directly
      generateAndShowCode();
    }
  };

  const formatLastSession = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Button - compact mode for dark control bar, normal for elsewhere */}
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleButtonClick}
        style={{
          cursor: 'pointer',
          ...(compact && {
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.15)',
            transition: 'background 0.2s ease'
          })
        }}
        title="Invite friend"
      >
        {!compact ? (
          <div className="control-button">
            {isGenerating ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="32" />
                </svg>
              </motion.div>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="white"/>
              </svg>
            )}
          </div>
        ) : (
          // Compact version for dark control bar
          isGenerating ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="32" />
              </svg>
            </motion.div>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="white"/>
            </svg>
          )
        )}
      </motion.div>

      {/* Buddy Picker Modal - FaceTime style */}
      <AnimatePresence>
        {showBuddyPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowBuddyPicker(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '16px'
            }}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#1c1c1e',
                borderRadius: '20px',
                width: '100%',
                maxWidth: '400px',
                maxHeight: '70vh',
                overflow: 'hidden',
                marginBottom: 'env(safe-area-inset-bottom, 0px)'
              }}
            >
              {/* Header */}
              <div style={{
                padding: '20px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center'
              }}>
                <h3 style={{ margin: 0, color: 'white', fontSize: '18px', fontWeight: 600 }}>
                  Study Together
                </h3>
                <p style={{ margin: '8px 0 0', color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                  Invite a friend to join your session
                </p>
              </div>

              {/* Recent Buddies List */}
              <div style={{
                padding: '12px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {loadingBuddies ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.5)' }}>
                    Loading...
                  </div>
                ) : recentBuddies.length > 0 ? (
                  <>
                    <div style={{
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.5)',
                      padding: '8px 12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Recent Study Buddies
                    </div>
                    {recentBuddies.map((buddy) => (
                      <motion.div
                        key={buddy.id}
                        whileHover={{ background: 'rgba(255,255,255,0.1)' }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => inviteBuddyDirect(buddy)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                      >
                        {/* Avatar */}
                        <div style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '50%',
                          background: buddy.avatar_color || '#297A92',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '18px',
                          fontWeight: 600
                        }}>
                          {buddy.display_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>

                        {/* Info */}
                        <div style={{ flex: 1 }}>
                          <div style={{
                            color: 'white',
                            fontSize: '16px',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            {buddy.display_name}
                            {buddy.is_favorite && (
                              <span style={{ color: '#fbbf24' }}>★</span>
                            )}
                          </div>
                          <div style={{
                            color: 'rgba(255,255,255,0.5)',
                            fontSize: '13px',
                            marginTop: '2px'
                          }}>
                            {buddy.total_sessions} sessions • {formatLastSession(buddy.last_session_at)}
                          </div>
                        </div>

                        {/* Invite indicator */}
                        {invitingBuddy === buddy.id ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                            style={{ color: 'rgba(255,255,255,0.5)' }}
                          >
                            ⏳
                          </motion.div>
                        ) : (
                          <div style={{
                            background: '#30d158',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 600,
                            padding: '6px 12px',
                            borderRadius: '16px'
                          }}>
                            Invite
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '14px'
                  }}>
                    No recent study buddies yet.
                    <br />
                    Generate a code to invite someone new!
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{
                padding: '16px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                gap: '12px'
              }}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={generateAndShowCode}
                  style={{
                    flex: 1,
                    background: '#297A92',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '14px',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {isGenerating ? 'Generating...' : 'New Code'}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowBuddyPicker(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '14px 20px',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Code Toast notification */}
      <AnimatePresence>
        {showToast && studyCode && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            style={{
              position: 'fixed',
              bottom: '90px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#1f2937',
              color: 'white',
              padding: '16px 24px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              zIndex: 2000
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', opacity: 0.8, marginBottom: '4px' }}>
                Your session code:
              </div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', letterSpacing: '4px' }}>
                {studyCode}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={copyCode}
                style={{
                  background: '#10b981',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Copy
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={shareCode}
                style={{
                  background: '#3b82f6',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Share
              </motion.button>
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                setShowToast(false);
                if (onCodeToastChange) {
                  onCodeToastChange(false);
                }
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '4px 8px'
              }}
            >
              ×
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Direct Invite Success Toast */}
      <AnimatePresence>
        {showToast && !studyCode && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            style={{
              position: 'fixed',
              bottom: '90px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#10b981',
              color: 'white',
              padding: '16px 24px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              zIndex: 2000
            }}
          >
            <span style={{ fontSize: '24px' }}>✓</span>
            <div>
              <div style={{ fontWeight: 600 }}>Invite link copied!</div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>Send it to your friend</div>
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                setShowToast(false);
                if (onCodeToastChange) {
                  onCodeToastChange(false);
                }
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '4px 8px'
              }}
            >
              ×
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StudyCallButton;
